import React from "react";
import { AlbumLike } from "../../types/album";
import OverlayRoot from "./OverlayRoot";

interface AlbumLikesModalProps {
  isOpen: boolean;
  onClose: () => void;
  likes: AlbumLike[];
  albumName: string;
  isLoading?: boolean;
  isLikedByCurrentUser: boolean;
  onLikeToggle: () => void;
  isLikeToggling?: boolean;
  canLike?: boolean;
}

const AlbumLikesModal: React.FC<AlbumLikesModalProps> = ({
  isOpen,
  onClose,
  likes,
  albumName,
  isLoading = false,
  isLikedByCurrentUser,
  onLikeToggle,
  isLikeToggling = false,
  canLike = true,
}) => {
  if (!isOpen) return null;

  return (
    <OverlayRoot data-section="album-likes-modal-root" className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm overscroll-none">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md jp-sheet rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
          {/* Header */}
          <div className="bg-black/20 backdrop-blur-xl border-b border-white/10 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Likes</h2>
                <p className="text-gray-300 text-sm">{albumName}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onLikeToggle}
                  disabled={!canLike || isLoading || isLikeToggling}
                  className="relative text-gray-300 hover:text-white p-2 hover:bg-white/10 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={canLike ? (isLikedByCurrentUser ? "Unlike album" : "Like album") : "Sign in to like this album"}
                >
                  <svg
                    className={`w-6 h-6 transition-colors duration-200 ${
                      isLikedByCurrentUser ? "text-red-400 fill-current" : "text-gray-300"
                    }`}
                    fill={isLikedByCurrentUser ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {isLikeToggling && (
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </button>

                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-200 p-2 hover:bg-white/10 rounded-xl transition-all duration-200"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto overscroll-contain">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : likes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6">
                <button
                  onClick={onLikeToggle}
                  disabled={!canLike || isLikeToggling}
                  className="relative w-16 h-16 bg-white/10 hover:bg-white/15 rounded-full flex items-center justify-center mb-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={canLike ? (isLikedByCurrentUser ? "Unlike album" : "Like album") : "Sign in to like this album"}
                >
                  <svg
                    className={`w-8 h-8 transition-colors duration-200 ${
                      isLikedByCurrentUser ? "text-red-400 fill-current" : "text-gray-400"
                    }`}
                    fill={isLikedByCurrentUser ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {isLikeToggling && (
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </button>
                <h3 className="text-lg font-semibold text-white mb-2">No likes yet</h3>
                <p className="text-gray-400 text-sm text-center">
                  Be the first to like this album!
                </p>
              </div>
            ) : (
              <div className="py-4">
                {likes.map((like) => (
                  <div key={like.id} className="flex items-center gap-3 px-6 py-3 hover:bg-white/5 transition-colors">
                    {/* User Avatar Placeholder */}
                    <div className="w-10 h-10 jp-gradient-brand rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">
                        {like.userEmail.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {like.userEmail}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {new Date(like.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Like Icon */}
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-red-400 fill-current" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {likes.length > 0 && (
            <div className="bg-black/20 backdrop-blur-xl border-t border-white/10 px-6 py-3">
              <p className="text-gray-300 text-sm text-center">
                {likes.length} {likes.length === 1 ? "person likes" : "people like"} this album
              </p>
            </div>
          )}
        </div>
      </div>
    </OverlayRoot>
  );
};

export default AlbumLikesModal;